import tinycolor from 'tinycolor2';

import type { BuiltInColorMap } from '../color-map';

/* regular expressions obtained from CssColourValidator.java
 * CssColourValidator runs in legacy Color Scheme on updateSiteLookAndFeel mutation
 * instead of validating on Save, we are now validating as user enters custom input
 */
const VAR_MATCH = new RegExp('^var\\((\\-\\-[a-zA-Z0-9\\-_]+)\\s*(,(.+))?\\)$');
const SHORT_NUMERIC_MATCH = new RegExp('^#([\\da-fA-F]){3}$');
const LONG_NUMERIC_MATCH = new RegExp('^#([\\da-fA-F]){6}$');
const RGB_MATCH = new RegExp(
	'^rgb\\(\\s*(0|[1-9]\\d?|1\\d\\d?|2[0-4]\\d|25[0-5])\\s*,\\s*(0|[1-9]\\d?|1\\d\\d?|2[0-4]\\d|25[0-5])\\s*,\\s*(0|[1-9]\\d?|1\\d\\d?|2[0-4]\\d|25[0-5])\\s*\\)$',
);
const PERC_MATCH = new RegExp('^rgb\\((\\d+)%,\\s*(\\d+)%,\\s*(\\d+)%\\)$');
const RGBA_MATCH = new RegExp(
	'^rgba\\(\\s*(0|[1-9]\\d?|1\\d\\d?|2[0-4]\\d|25[0-5])\\s*,\\s*(0|[1-9]\\d?|1\\d\\d?|2[0-4]\\d|25[0-5])\\s*,\\s*(0|[1-9]\\d?|1\\d\\d?|2[0-4]\\d|25[0-5])\\s*,\\s*((0.[0-9]*)|[01])\\s*\\)$',
);
const HSL_MATCH = new RegExp(
	'^hsl\\(\\s*(0|[1-9]\\d?|[12]\\d\\d|3[0-5]\\d)\\s*,\\s*((0|[1-9]\\d?|100)%)\\s*,\\s*((0|[1-9]\\d?|100)%)\\s*\\)$',
);

/* evaluates validity for any incoming colors from 1) siteConfiguration query 2) custom input field
 * until hsla and named color conversion errors are handled properly in app-navigation, we return the default Atlassian theme as in getSanitizedColor fn
 * it is an existing pattern under useNavigationTheme to return the theme as means of error handling
 * once resolved, this isValidColor can validate for HSLA_MATCH and NAMED_COLOR_MATCH
 */

function isValidVarColor(color: string): boolean {
	const matches = VAR_MATCH.exec(color);

	if (matches) {
		const fallback = matches[3]?.trim();

		if (fallback) {
			// If fallback is another var() call, validate it recursively
			if (fallback.startsWith('var(')) {
				return isValidVarColor(fallback);
			}
			// Otherwise validate as a regular color
			return isValidColor(fallback, false);
		}

		// If there's a comma but no fallback (like "var(--token, )"), it's invalid
		if (matches[2] && !fallback) {
			return false;
		}

		return true;
	}

	return false;
}

export function isValidColor(color: string, isVarColorAllowed = true): boolean {
	return (
		(isVarColorAllowed && isValidVarColor(color)) ||
		SHORT_NUMERIC_MATCH.test(color) ||
		LONG_NUMERIC_MATCH.test(color) ||
		RGB_MATCH.test(color) ||
		PERC_MATCH.test(color) ||
		RGBA_MATCH.test(color) ||
		HSL_MATCH.test(color)
	);
}

/* returns tinycolor instance allowing color validation from ColorInput into HueAndSaturation
 * HueAndSaturation requires conversion from any color to hsv and hsl using tinycolor
 * if valid, tinycolor instance for color from ColorInput returned to HueAndSaturation. Else, component receives instance of previously staged / saved / default Atlassian color
 * tokenized color: obtains computed hex value and returns instance of such value or fallback if provided
 * any other colors: if valid, instance returned of any color. If not, returns invalid instance
 */
export function convertLegacyColor(color: string): ReturnType<typeof tinycolor> {
	if (!isValidColor(color)) {
		return tinycolor('invalid');
	}
	const colorCode = extractColorCode(color);
	return tinycolor(colorCode);
}

/* simply extracts the color code from given color string.
 * If color is tokenized, either computes or extracts fallback hex value
 * Else return color as is
 */
function extractColorCode(color: string): string {
	const matches = VAR_MATCH.exec(color);

	if (matches) {
		const fallback = matches[3]?.trim();
		const token = matches[1].toLowerCase();

		// SSR guard
		if (typeof window !== 'undefined' && typeof document !== 'undefined') {
			const compiledTokenValue = window
				.getComputedStyle(document.documentElement)
				.getPropertyValue(token)
				.trim();

			// If we have a computed value, return it
			if (compiledTokenValue) {
				return compiledTokenValue;
			}
		}

		// If there's a fallback, process it recursively
		if (fallback) {
			// If fallback is another var() call, extract from it recursively
			if (fallback.startsWith('var(')) {
				return extractColorCode(fallback);
			}
			// Otherwise return the fallback as is
			return fallback;
		}

		// No computed value and no fallback
		return '';
	}

	return color;
}

/* simply extracts the "--ds-background-..." part of the tokenized color string "var(--ds-background..."
 * extractor is needed to assess equivalence of two tokenized colors, independent from the presence of fallback string
 */
export function extractToken(color: string | undefined = ''): string {
	const matches = VAR_MATCH.exec(color);

	return matches?.[1].toLowerCase() ?? color;
}

/* CustomPalette: any tokenized colors should map to equivalent curated palette tile, if the equivalent is available.
 *	if so, equivalent token from curated palette is returned. If not, color is returned as is, and will be added as first of nine tiles in curated palette
 * ColorField: same as for CustomPalette. Also evaluates for the validity in case of hsla and named colors, in which case default Atlassian theme is returned.
 */
export function getSanitizedColor(
	{ allColors, allTokens, knownColors }: BuiltInColorMap,
	color: string | undefined = '',
): string {
	const index = allTokens.indexOf(extractToken(color));
	if (index !== -1) {
		return allColors[index];
	}
	if (!isValidColor(color)) {
		return knownColors[0];
	}

	return color;
}

/* Ensures that background and highlight color combinations provide sufficient contrast in support of AA standard for accessibility */
export function isReadable(backgroundColor: string, highlightColor: string): boolean {
	const backgroundCode = extractColorCode(backgroundColor);
	const highlightCode = extractColorCode(highlightColor);
	return tinycolor.isReadable(backgroundCode, highlightCode);
}

export function getMostReadableColor(color: string): string {
	color = extractColorCode(color);
	return tinycolor.mostReadable(color, ['#000000', '#ffffff']).toHexString();
}

export function isEquivalentColor(color1: string, color2: string): boolean {
	return extractColorCode(color1) === extractColorCode(color2);
}
